/**
 * WDS3 Cache — in-memory store for V3 Data Editor.
 * Populated by WDS3DataEditor.vue after downloading game files from GitHub raw URLs.
 * Consumed by WDS2UnitEditor.vue when running in V3 mode (history.state.wds3Mode === true).
 */

const SESSION_KEY = 'wds3CacheSession';

export const wds3Cache = {
  /** ID of the selected game */
  gameId: null,
  /** Display name of the selected game */
  gameName: null,
  /** Base URL for all file paths, e.g. "https://raw.githubusercontent.com/.../GameName/" */
  dataBasePath: null,
  /** GitHub branch name extracted from dataBasePath */
  gitBranch: null,
  /** GitHub owner/repo extracted from dataBasePath */
  gitOwnerRepo: null,
  /** Parsed manifest.json object */
  manifest: null,
  /**
   * Root-level game files, keyed by filename.
   * e.g. { 'rules.json': [...], 'bases.json': [...], 'hierarchy.json': {...} }
   */
  gameFiles: {},
  /**
   * Faction subdirectories, each with their own file map.
   * e.g. [{ name: 'Sample Army', files: { 'units.json': [...], 'profiles.json': [...] } }]
   * The `files` object is also used directly as the faction "handle" in WDS2UnitEditor V3 mode.
   */
  factions: [],
  /**
   * Paths of files that were removed from the cache (e.g. after clearing a faction).
   * These need to be deleted from GitHub on the next upload.
   * e.g. ['Sample Army/units.json', 'Sample Army/profiles.json']
   */
  deletedFiles: [],
  /**
   * The manifest version at the time this session was loaded from Git.
   * Edits are "unpublished" until a successful git upload bumps this.
   */
  baseVersion: null,

  /** Persist the current cache to sessionStorage so a page refresh can restore it. */
  saveToSession() {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        gameId: this.gameId,
        gameName: this.gameName,
        dataBasePath: this.dataBasePath,
        gitBranch: this.gitBranch,
        gitOwnerRepo: this.gitOwnerRepo,
        manifest: this.manifest,
        gameFiles: this.gameFiles,
        factions: this.factions,
        deletedFiles: this.deletedFiles,
        baseVersion: this.baseVersion,
      }));
    } catch (e) {
      console.warn('wds3Cache: could not save to sessionStorage', e);
    }
  },

  /**
   * Restore cache from sessionStorage.
   * Returns true if a valid session was found and applied, false otherwise.
   */
  restoreFromSession() {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return false;
      const payload = JSON.parse(raw);
      if (!payload || !payload.gameId) return false;
      this.gameId      = payload.gameId;
      this.gameName    = payload.gameName;
      this.dataBasePath = payload.dataBasePath;
      this.gitBranch    = payload.gitBranch || 'main';
      this.gitOwnerRepo = payload.gitOwnerRepo || null;
      this.manifest    = payload.manifest;
      this.gameFiles   = payload.gameFiles   || {};
      this.factions    = payload.factions    || [];
      this.deletedFiles = payload.deletedFiles || [];
      this.baseVersion  = payload.baseVersion ?? null;
      
      // Fallback: if baseVersion is null but manifest has a version, use it
      if (this.baseVersion === null && this.manifest && this.manifest.version) {
        console.log('[wds3Cache] Restoring baseVersion from manifest:', this.manifest.version);
        this.baseVersion = this.manifest.version;
      }
      
      console.log('[wds3Cache] Restored from session:', {
        gameId: this.gameId,
        baseVersion: this.baseVersion,
        manifestVersion: this.manifest?.version
      });
      
      return true;
    } catch (e) {
      console.warn('wds3Cache: could not restore from sessionStorage', e);
      return false;
    }
  },
};
